import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money, fmtDate, onlyDigits } from "@/lib/api";
import Money, { PrivacyToggle } from "@/components/Money";
import DuplicatePhoneModal from "@/components/DuplicatePhoneModal";
import { findPhoneMatch } from "@/lib/clientHelpers";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MessageCircle, Plus, CheckCircle2, Trash2, Phone, MapPin, Pencil, Save, X, CalendarClock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ClienteDetalhe() {
  const { id } = useParams();
  const nav = useNavigate();
  const { session, isAdmin, isVendedor, isCobrador } = useAuth();
  const [cli, setCli] = useState(null);
  const [payFor, setPayFor] = useState(null); // parcela obj
  const [payValue, setPayValue] = useState("");
  const [payMethod, setPayMethod] = useState("dinheiro");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ nome: "", telefone: "", bairro: "", endereco: "", observacoes: "", rota_id: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const [cobradores, setCobradores] = useState([]);
  const [rotas, setRotas] = useState([]);

  const load = async () => {
    const r = await api.get(`/clientes/${id}`);
    setCli(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (isAdmin) {
      api.get("/staff").then((r) => setCobradores(r.data.filter((s) => s.roles?.cobrador && s.active))).catch(() => {});
    }
    api.get("/rotas").then((r) => setRotas(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!cli) return <div className="text-stone-500 text-sm">Carregando…</div>;

  const totalDevedor = cli.vendas.reduce(
    (a, v) => a + v.parcelas.reduce((s, p) => s + Math.max(0, p.valor - p.valor_pago), 0),
    0
  );

  // Agrupa todas as remarcações de todas as parcelas em ordem cronológica desc
  const remarcacoesList = [];
  cli.vendas.forEach((v) => {
    v.parcelas.forEach((p) => {
      (p.remarcacoes || []).forEach((rem) => {
        remarcacoesList.push({
          ...rem,
          venda_descricao: v.descricao,
          parcela_numero: p.numero,
          total_parcelas: v.num_parcelas,
          parcela_valor: p.valor,
        });
      });
    });
  });
  remarcacoesList.sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  const openPay = (venda, parcela) => {
    setPayFor({ venda, parcela });
    setPayValue(String((parcela.valor - parcela.valor_pago).toFixed(2)));
    setPayMethod("dinheiro");
  };

  const doPay = async () => {
    const { venda, parcela } = payFor;
    const paidNow = parseFloat(payValue);
    if (!(paidNow > 0)) return toast.error("Informe um valor válido");
    try {
      await api.post(`/vendas/${venda.id}/parcelas/${parcela.id}/pagar`, {
        valor_pago: paidNow,
        metodo_pagamento: payMethod,
      });
      toast.success("Baixa registrada");
      const phone = onlyDigits(cli.telefone);
      if (phone) {
        const resta = Math.max(0, (parcela.valor - parcela.valor_pago) - paidNow);
        try {
          const t = await api.get("/whatsapp/template", {
            params: {
              tipo: "recebido",
              cliente: cli.nome,
              parcela: `${parcela.numero}/${venda.num_parcelas}`,
              valor: paidNow,
              resta,
              metodo: payMethod,
            },
          });
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(t.data.mensagem)}`, "_blank");
        } catch (_e) { /* ignore template error */ }
      }
      setPayFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const sendWhats = async (venda, parcela) => {
    const phone = onlyDigits(cli.telefone);
    if (!phone) return toast.error("Cliente sem telefone");
    const tipo = parcela.status === "atrasada" ? "atrasado" : parcela.status === "paga" ? "obrigado" : "lembrete";
    const t = await api.get("/whatsapp/template", {
      params: {
        tipo,
        cliente: cli.nome,
        parcela: `${parcela.numero}/${venda.num_parcelas}`,
        valor: parcela.valor - parcela.valor_pago,
        vencimento: parcela.vencimento,
      },
    });
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(t.data.mensagem)}`, "_blank");
  };

  const removeVenda = async (vid) => {
    if (!window.confirm("Excluir esta venda?")) return;
    await api.delete(`/vendas/${vid}`);
    toast.success("Venda excluída");
    load();
  };

  const openEdit = () => {
    setEditForm({
      nome: cli.nome || "",
      telefone: cli.telefone || "",
      bairro: cli.bairro || "",
      endereco: cli.endereco || "",
      observacoes: cli.observacoes || "",
      rota_id: cli.rota_id || "",
    });
    setEditing(true);
  };

  const persistEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/clientes/${cli.id}`, { ...editForm, rota_id: editForm.rota_id || null });
      toast.success("Cliente atualizado");
      setEditing(false);
      setDupMatch(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = async () => {
    if (!editForm.nome.trim()) return toast.error("Nome é obrigatório");
    if (editForm.telefone && editForm.telefone !== cli.telefone) {
      const match = await findPhoneMatch(editForm.telefone, cli.id);
      if (match) {
        setDupMatch(match);
        return;
      }
    }
    persistEdit();
  };

  return (
    <div>
      <button onClick={() => nav("/clientes")} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="carne-card p-5 mb-5">
        {!editing ? (
          <>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] flex items-center justify-center font-display text-2xl font-bold shrink-0">
                {cli.nome[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h1 data-testid="client-detail-name" className="font-display text-2xl font-extrabold tracking-tight text-stone-900">{cli.nome}</h1>
                <div className="text-sm text-stone-500 space-y-0.5 mt-1">
                  {cli.telefone && <div className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{cli.telefone}</div>}
                  {cli.bairro && <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{cli.bairro}{cli.endereco ? ` — ${cli.endereco}` : ""}</div>}
                  {cli.rota_id && rotas.find((r) => r.id === cli.rota_id) && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] px-1.5 py-0.5 rounded mt-1">
                      Rota: {rotas.find((r) => r.id === cli.rota_id).nome} • {["SEG","TER","QUA","QUI","SEX","SAB","DOM"][rotas.find((r) => r.id === cli.rota_id).dia_semana]}
                    </div>
                  )}
                </div>
                {cli.observacoes && (
                  <div className="text-xs text-stone-500 mt-2 italic">{cli.observacoes}</div>
                )}
              </div>
              <div className="flex flex-col gap-2 items-end">
                <PrivacyToggle />
                {(isAdmin || (isVendedor && cli.created_by === session?.staff?.id)) && (
                  <button
                    data-testid="btn-edit-client"
                    onClick={openEdit}
                    className="h-9 px-3 rounded-full bg-stone-100 text-stone-700 text-xs font-bold inline-flex items-center gap-1 hover:bg-stone-200"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-white rounded-xl p-3 border border-stone-200">
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Saldo devedor</div>
                <div className="font-mono-num text-xl font-bold text-stone-900"><Money v={totalDevedor} /></div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-stone-200">
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Total vendas</div>
                <div className="font-mono-num text-xl font-bold text-stone-900">{cli.vendas.length}</div>
              </div>
            </div>
            {remarcacoesList.length > 2 && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 inline-flex items-center gap-2" data-testid="cli-reincidente-badge">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                <div className="text-xs">
                  <b className="text-amber-900">Cliente reincidente</b> — {remarcacoesList.length} remarcações no total
                </div>
              </div>
            )}
            {cli.telefone && (
              <a
                data-testid="btn-whatsapp-direct"
                href={`https://wa.me/${onlyDigits(cli.telefone)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp w-full mt-3 justify-center"
              >
                <MessageCircle className="w-4 h-4" /> Abrir no WhatsApp
              </a>
            )}
          </>
        ) : (
          <div data-testid="edit-client-form" className="space-y-3">
            <h2 className="font-display text-lg font-bold text-stone-900">Editar cliente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="edit-nome" placeholder="Nome*" value={editForm.nome} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
              <input data-testid="edit-telefone" placeholder="WhatsApp (5511...)" value={editForm.telefone} onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
              <input data-testid="edit-bairro" placeholder="Bairro" value={editForm.bairro} onChange={(e) => setEditForm({ ...editForm, bairro: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
              <input data-testid="edit-endereco" placeholder="Endereço" value={editForm.endereco} onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })} className="h-12 px-3 rounded-xl border border-stone-200 bg-white" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Rota de cobrança</label>
              <select
                data-testid="edit-rota"
                value={editForm.rota_id}
                onChange={(e) => setEditForm({ ...editForm, rota_id: e.target.value })}
                className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
              >
                <option value="">— Sem rota —</option>
                {rotas.map((r) => (
                  <option key={r.id} value={r.id}>{r.nome} ({["SEG","TER","QUA","QUI","SEX","SAB","DOM"][r.dia_semana]})</option>
                ))}
              </select>
              {rotas.length > 0 && cli.rota_id !== editForm.rota_id && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2">
                  Ao mudar a rota, apenas parcelas futuras ainda não pagas serão recalculadas.
                </div>
              )}
            </div>
            <textarea data-testid="edit-obs" placeholder="Observações" value={editForm.observacoes} onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} className="w-full min-h-[70px] p-3 rounded-xl border border-stone-200 bg-white" />
            <div className="flex gap-2">
              <button
                data-testid="btn-cancel-edit"
                type="button"
                onClick={() => setEditing(false)}
                className="h-12 px-4 rounded-xl font-bold text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
              >
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button
                data-testid="btn-save-edit"
                type="button"
                onClick={saveEdit}
                disabled={savingEdit}
                className="btn-primary flex-1"
              >
                <Save className="w-4 h-4" /> Salvar alterações
              </button>
            </div>
          </div>
        )}
      </div>

      {remarcacoesList.length > 0 && (
        <section className="carne-card p-4 mb-5" data-testid="cli-remarcacoes-timeline">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <h2 className="font-display text-base font-bold text-stone-900 inline-flex items-center gap-1">
              <CalendarClock className="w-4 h-4 text-amber-700" /> Histórico de remarcações ({remarcacoesList.length})
            </h2>
            {isAdmin && (
              <button
                data-testid="btn-liberar-remarcacao"
                onClick={async () => {
                  if (!window.confirm(`Liberar +1 remarcação extra para ${cli.nome}?`)) return;
                  try {
                    const r = await api.post(`/clientes/${cli.id}/liberar-remarcacao`);
                    toast.success(`Liberado — total de extras: ${r.data.remarcacoes_extra_permitidas}`);
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Erro");
                  }
                }}
                className="h-9 px-3 rounded-full bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300 inline-flex items-center gap-1 hover:bg-amber-200"
                title="Permite ao cobrador fazer mais uma remarcação mesmo com limite atingido"
              >
                <Plus className="w-3.5 h-3.5" /> Liberar nova remarcação
                {(cli.remarcacoes_extra_permitidas || 0) > 0 && (
                  <span className="ml-1 bg-amber-900 text-amber-50 text-[10px] rounded-full px-1.5" data-testid="extras-count">
                    +{cli.remarcacoes_extra_permitidas}
                  </span>
                )}
              </button>
            )}
          </div>
          <ol className="relative border-l-2 border-amber-200 ml-2 space-y-3">
            {remarcacoesList.map((rem) => (
              <li key={rem.id} className="pl-4 relative" data-testid={`rem-item-${rem.id}`}>
                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-amber-400 border-2 border-white"></span>
                <div className="text-[11px] font-mono-num text-stone-500">
                  {rem.at ? new Date(rem.at).toLocaleString("pt-BR") : "—"}
                </div>
                <div className="text-sm text-stone-900 mt-0.5">
                  <b>Parc. {rem.parcela_numero}/{rem.total_parcelas}</b> ({rem.venda_descricao}) · <Money v={rem.parcela_valor} />
                </div>
                <div className="text-xs text-stone-700 mt-0.5">
                  De <b>{fmtDate(rem.data_original)}</b> → <b className="text-amber-900">{fmtDate(rem.nova_data)}</b>
                </div>
                <div className="text-xs text-stone-600 mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1 italic">"{rem.motivo}"</div>
                {rem.por_name && <div className="text-[11px] text-stone-500 mt-1">— {rem.por_name}</div>}
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold text-stone-900">Vendas & Parcelas</h2>
        {(isAdmin || isVendedor) && (
          <Link to={`/nova-venda?cliente=${cli.id}`} className="btn-primary text-sm py-2 px-3">
            <Plus className="w-4 h-4" /> Nova venda
          </Link>
        )}
      </div>

      {isAdmin && (
        <div className="carne-card p-4 mb-4" data-testid="assign-cobrador-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Cobrador responsável</div>
          <select
            data-testid="select-assign-cobrador"
            value={cli.assigned_cobrador_id || ""}
            onChange={async (e) => {
              const v = e.target.value || null;
              try {
                await api.put(`/clientes/${cli.id}/cobrador`, { cobrador_id: v });
                toast.success(v ? "Cobrador atribuído" : "Cobrador removido");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro");
              }
            }}
            className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white"
          >
            <option value="">— Nenhum —</option>
            {cobradores.map((c) => (
              <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>
            ))}
          </select>
          {cli.created_by_name && (
            <div className="text-[11px] text-stone-500 mt-2">Cadastrado por <b>{cli.created_by_name}</b></div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {cli.vendas.length === 0 && <div className="text-sm text-stone-500 py-6 text-center">Nenhuma venda registrada.</div>}
        {cli.vendas.map((v) => (
          <div key={v.id} className="carne-card p-4" data-testid={`venda-${v.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="font-display text-base font-bold text-stone-900">{v.descricao}</div>
                <div className="text-xs text-stone-500">
                  Total <Money v={v.valor_total} /> • Entrada <Money v={v.entrada} /> • {v.num_parcelas}x {v.frequencia}
                </div>
              </div>
              <button data-testid={`btn-del-venda-${v.id}`} onClick={() => removeVenda(v.id)} className={`text-stone-400 hover:text-rose-600 p-1 ${!isAdmin ? "hidden" : ""}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-stone-100">
              {v.parcelas.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 py-3" data-testid={`installment-item-${p.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-900">Parcela {p.numero}/{v.num_parcelas}</span>
                      {p.status === "paga" && <span className="stamp-paid">Paga</span>}
                      {p.status === "atrasada" && <span className="stamp-overdue">Atrasada</span>}
                      {p.status === "parcial" && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Parcial</span>}
                    </div>
                    <div className="text-xs text-stone-500">
                      Vence {fmtDate(p.vencimento)}
                      {p.data_cobranca && p.data_cobranca !== p.vencimento && (
                        <span className="ml-1 text-[color:var(--terracotta)] font-bold">• Cobra {fmtDate(p.data_cobranca)}</span>
                      )}
                      {" • "}Pago <Money v={p.valor_pago} />
                      {p.metodo_pagamento && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">
                          {{dinheiro: "💵 Dinheiro", pix: "⚡ Pix", credito: "💳 Crédito", debito: "💳 Débito"}[p.metodo_pagamento]}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-sm font-bold text-stone-900"><Money v={p.valor} /></div>
                    <div className="flex gap-1 mt-1">
                      {p.status !== "paga" && (isAdmin || isCobrador) && (
                        <button
                          data-testid={`btn-mark-paid-${p.id}`}
                          onClick={() => openPay(v, p)}
                          className="text-xs font-bold text-emerald-700 hover:underline inline-flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Dar baixa
                        </button>
                      )}
                      <button
                        data-testid={`btn-whats-parc-${p.id}`}
                        onClick={() => sendWhats(v, p)}
                        className="text-xs font-bold text-[color:var(--whatsapp-dark)] hover:underline inline-flex items-center gap-1"
                      >
                        <MessageCircle className="w-3 h-3" /> Zap
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {payFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setPayFor(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="modal-payment">
            <h3 className="font-display text-lg font-bold text-stone-900 mb-2">Dar baixa na parcela {payFor.parcela.numero}</h3>
            <p className="text-sm text-stone-500 mb-3">Valor em aberto: <Money v={payFor.parcela.valor - payFor.parcela.valor_pago} /></p>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Valor recebido (R$)</label>
            <input
              data-testid="input-payment-value"
              type="number"
              step="0.01"
              value={payValue}
              onChange={(e) => setPayValue(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
            />
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Forma de pagamento</label>
              <div className="mt-1 grid grid-cols-2 gap-2" data-testid="payment-method-group">
                {[
                  { k: "dinheiro", label: "Dinheiro", emoji: "💵" },
                  { k: "pix", label: "Pix", emoji: "⚡" },
                  { k: "credito", label: "Crédito", emoji: "💳" },
                  { k: "debito", label: "Débito", emoji: "💳" },
                ].map(({ k, label, emoji }) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`btn-method-${k}`}
                    onClick={() => setPayMethod(k)}
                    className={`h-12 rounded-xl border-2 font-bold text-sm inline-flex items-center justify-center gap-1 transition-colors ${
                      payMethod === k
                        ? "border-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]"
                        : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                    }`}
                  >
                    <span>{emoji}</span> {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPayFor(null)} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
              <button data-testid="btn-confirm-payment" onClick={doPay} className="btn-primary flex-1">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      <DuplicatePhoneModal
        open={!!dupMatch}
        existing={dupMatch}
        mode="edit"
        onUseExisting={() => {}}
        onCreateAnyway={() => { setDupMatch(null); persistEdit(); }}
        onCancel={() => setDupMatch(null)}
      />
    </div>
  );
}
